import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
    Box,
    ClickAwayListener,
    Collapse,
    IconButton,
    ListItem,
    Popper,
    Stack,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { DrawerNavigationItem } from 'c-wrapper/Types/DrawerConfiguration';
import { ListItemIcon, ListItemText, MuiRouterLink } from 'c-components';
import { Translate } from 'c-translation';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions } from 'c-auth-module/Components';

type Props = {
    item: DrawerNavigationItem;
    inSubList?: boolean;
    onNavigate?: () => void;
};

const useStyles = makeStyles(theme => ({
    active: {
        borderBottom: `5px solid ${theme.palette.primary.main}`,
        [theme.breakpoints.down('sm')]: {
            borderBottom: 'none',
            borderLeft: `5px solid ${theme.palette.primary.main}`,
        },
    },
    semiActive: {
        borderBottom: `5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        [theme.breakpoints.down('sm')]: {
            borderBottom: 'none',
            borderLeft: `5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        },
    },
    mobileItem: {
        padding: theme.spacing(0.5),
    },
    mobileChildrenContainer: {
        paddingLeft: theme.spacing(4),
    },
}));

const DrawerNavigationListItem: React.FC<Props> = props => {
    const { pathname } = useLocation();
    const {
        item: { link, items = {} },
    } = props;
    const isActive = useMemo(() => {
        if (link != null && pathname.startsWith(link)) {
            return true;
        }

        if (items && typeof items === 'object') {
            return Object.values(items).some(
                child => child.link != null && pathname.startsWith(child.link),
            );
        }

        return false;
    }, [pathname, link, items]);

    /**
     * we want to stop re rendering the list item every time the pathname changes
     * unless the isActive boolean check has changed
     */
    return <MemoizedItem {...props} isActive={isActive} />;
};

type ItemProps = Props & {
    isActive: boolean;
};
const MemoizedItem = React.memo<ItemProps>(function DrawerNavigationItem({
    isActive,
    inSubList,
    onNavigate,
    item: { link, items, label, icon, id, hideIfNoChildren },
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const anchorRef = useRef<HTMLLIElement>(null);
    const { hasAll } = useUserPermissions();
    const { mobileItem, mobileChildrenContainer } = useStyles();
    const [expanded, setExpanded] = useState(false);
    const childItems = useMemo(() => {
        const visibleChildItems =
            Object.entries(items ?? {})
                ?.map(x => x[1])
                ?.filter(item => hasAll(item.permissions)) ?? [];

        return items == null ||
            (visibleChildItems.length === 0 && hideIfNoChildren === true) ? undefined : (
            <DrawerNavigationGroup isSubList items={visibleChildItems} />
        );
    }, [items, hasAll, hideIfNoChildren]);

    const visibleChildItems = useMemo(
        () =>
            Object.entries(items ?? {})
                .map(x => x[1])
                .filter(item => hasAll(item.permissions)) ?? [],
        [items, hasAll],
    );

    const isOpen = useMemo(() => !!childItems && expanded, [childItems, expanded]);

    const onClickToggleOpen = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setExpanded(prev => !prev);
    }, []);

    const onClickNavigate = useCallback(() => {
        setExpanded(false);
        onNavigate?.();
    }, [onNavigate]);

    if (childItems == null && hideIfNoChildren) {
        return null;
    }

    return (
        <Box
            position="relative"
            display={isMobile || inSubList ? 'block' : 'flex'}
            width={isMobile ? '100%' : 'auto'}
            height={isMobile ? 'auto' : '100%'}
        >
            <ListItem
                id={`navigation-drawer-${id}`}
                button
                disablePadding={!isMobile}
                component={MuiRouterLink}
                to={link ?? ''}
                onClick={onClickNavigate}
                ref={anchorRef}
                color="inherit"
                selected={isActive}
                className={isMobile ? mobileItem : ''}
                sx={theme => ({
                    borderRadius: isMobile ? 1 : 0,
                    height: isMobile ? 'auto' : theme.spacing(8),
                    minHeight: isMobile ? 'auto' : theme.spacing(8),
                    alignItems: 'center',
                    ...(isActive && {
                        bgcolor: theme.palette.action.selected,
                        '&:hover': {
                            bgcolor: theme.palette.action.hover,
                        },
                    }),
                    ...(!isMobile && {
                        px: 1,
                        py: 0,
                        alignSelf: 'stretch',
                        my: 0,
                    }),
                    ...(isMobile && {
                        py: 1,
                        px: 1.5,
                        width: '100%',
                    }),
                })}
            >
                <ListItemIcon sx={{ fontSize: '1.25em', minWidth: 28 }}>{icon}</ListItemIcon>
                <Stack direction="row" gap={0.5} alignItems="center">
                    <ListItemText
                        primary={label && <Translate path={label} />}
                        primaryTypographyProps={{
                            sx: {
                                color: theme =>
                                    theme.palette.mode === 'dark'
                                        ? theme.palette.text.primary
                                        : theme.palette.grey[800],
                                paddingTop: 0,
                                fontSize: isMobile ? '0.75rem' : '0.8rem',
                                position: 'relative',
                                top: 1,
                            },
                        }}
                    />
                    {childItems && (
                        <IconButton
                            onClick={onClickToggleOpen}
                            size={isMobile ? 'small' : 'medium'}
                        >
                            {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    )}
                </Stack>
            </ListItem>

            {childItems && isMobile && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box
                        className={mobileChildrenContainer}
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: theme =>
                                theme.palette.mode === 'dark' ? theme.palette.divider : '#ccc',
                            ml: 2,
                            mt: 0.5,
                            mb: 0.5,
                            pl: 1,
                        }}
                    >
                        <DrawerNavigationGroup
                            isSubList
                            items={visibleChildItems}
                            onNavigate={onClickNavigate}
                        />
                    </Box>
                </Collapse>
            )}

            {childItems && !isMobile && inSubList && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: theme =>
                                theme.palette.mode === 'dark' ? theme.palette.divider : '#ccc',
                            ml: 2,
                            mt: 0.5,
                            mb: 0.5,
                            pl: 1,
                        }}
                    >
                        <DrawerNavigationGroup
                            isSubList
                            items={visibleChildItems}
                            onNavigate={onClickNavigate}
                        />
                    </Box>
                </Collapse>
            )}

            {childItems && !isMobile && !inSubList && (
                <Popper
                    open={isOpen}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1400 }}
                >
                    <ClickAwayListener onClickAway={() => setExpanded(false)}>
                        <Box
                            sx={{
                                mt: 0.5,
                                minWidth: 240,
                                maxHeight: 400,
                                overflowY: 'auto',
                                bgcolor: 'background.paper',
                                boxShadow: 4,
                                borderRadius: 1,
                                p: 1,
                            }}
                        >
                            <DrawerNavigationGroup
                                isSubList
                                items={visibleChildItems}
                                onNavigate={onClickNavigate}
                            />
                        </Box>
                    </ClickAwayListener>
                </Popper>
            )}
        </Box>
    );
});

// A group renders list items and an item renders a nested group, so both live in this module:
// split across two files they import each other (an import cycle).
type GroupProps = {
    items: DrawerNavigationItem[];
    isSubList?: boolean;
    onNavigate?: () => void;
};

const DrawerNavigationGroupInner: React.FC<GroupProps> = ({
    items,
    isSubList = false,
    onNavigate,
}) => {
    const { hasAll } = useUserPermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const visibleChildren = useMemo(
        () => items.filter(item => hasAll(item.permissions)),
        [hasAll, items],
    );

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : isSubList ? 'column' : 'row',
                gap: isSubList ? '8px' : '16px',
                width: isMobile ? '100%' : 'auto',
                alignItems: isMobile || isSubList ? 'initial' : 'stretch',
            }}
        >
            {visibleChildren.map(item => (
                <IfHasAllPermissions key={item.label} permissions={item.permissions}>
                    <DrawerNavigationListItem
                        key={item.label}
                        item={item}
                        inSubList={isSubList}
                        onNavigate={onNavigate}
                    />
                </IfHasAllPermissions>
            ))}
        </Box>
    );
};

export const DrawerNavigationGroup = React.memo(DrawerNavigationGroupInner);
